import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  Pipe,
  PipeTransform,
  QueryList,
  Renderer2,
  signal,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import moment from "moment";
import { UserFieldService } from "../../../services/user-field/user-field.service";
import { KeyValue, KeyValuePipe, NgClass } from "@angular/common";
import { DropdownComponent } from "../../utils/dropdown/dropdown.component";
import { escapeCSV, formatNumber } from "../../utils/data-util";
import { toSignal } from "@angular/core/rxjs-interop";

@Pipe({
  name: "visibleColumns",
  standalone: true,
})
export class VisibleColumnsPipe implements PipeTransform {
  transform(entries: KeyValue<string, boolean | undefined>[], ...args: any[]) {
    return entries.filter((pair) => pair.value);
  }
}

type ColumnFilterOptions = Array<{ key: string; label: string }>;
@Component({
  selector: "app-dashboard-table",
  standalone: true,
  imports: [DropdownComponent, VisibleColumnsPipe, KeyValuePipe, NgClass],
  templateUrl: "./dashboard-table.component.html",
  styleUrl: "./dashboard-table.component.scss",
})
export class DashboardTableComponent implements AfterViewInit, OnDestroy {
  userFieldService = inject(UserFieldService);
  renderer = inject(Renderer2);
  userFieldSelectedId = toSignal(this.userFieldService.selectedUserFieldId$, {
    initialValue: null,
  });
  allClaimedFields = input.required<ClaimedField[]>();
  originalOrder = () => 0; // ? to keep the original order keyvalue pipe - https://stackoverflow.com/questions/52793944/angular-keyvalue-pipe-sort-properties-iterate-in-order
  columnLabels = input<{ [key in keyof ClaimedField]?: string }>(); // column labels
  columnVisibility = model<Partial<Record<keyof ClaimedField, boolean>>>({
    field_name: true,
    field_id: true,
    entities: true,
    username: true,
    first_name: true,
    last_name: true,
    email: true,
    total_target_water: true,
    is_deficit_irrigation: false,
    crop_type_01: true,
    crop_type_02: false,
    crop_type_03: false,
    county: false,
  }); // column visibility settings
  getColumnLabel = (key: string) => {
    const labels = this.columnLabels?.();
    return (labels && labels[key as keyof ClaimedField]) ?? key;
  };
  // ! One off for values that doesn't need the field info
  formatColumnValueWithoutField = (column: string, value: any) => {
    if (value === null || value === undefined) {
      return "";
    }
    switch (column) {
      case "acres":
        return formatNumber(value, 1);
      case "entities":
        if (!Array.isArray(value) || value.length === 0) return "None";
        return value
          .map((e: { name: string; email: string }) => e.name)
          .join(", ");
      case "is_deficit_irrigation":
        return (
          value === "null" || value === null ? "None"
          : value === "true" || value === true ? "Yes"
          : "No"
        );
      default:
        return value;
    }
  };
  // ! without date and water shenanigans
  getColumnValue = (column: string, field: ClaimedField) => {
    if (!(column in field)) {
      return "";
    }
    switch (column) {
      case "crop_type_01":
      case "crop_type_02":
      case "crop_type_03":
        return !field[column as keyof ClaimedField] ?
            "None"
          : `${field[column as keyof ClaimedField]}`;
      case "entities":
      case "acres":
      case "is_deficit_irrigation":
        return this.formatColumnValueWithoutField(
          column,
          field[column as keyof ClaimedField],
        );
      default:
        return field[column as keyof ClaimedField] ?? "";
    }
  };

  columnListDropdownOpen = false;
  @ViewChild("dropdownButtonColumnList") dropdownButtonColumnList!: ElementRef;
  @ViewChild("dropdownMenuColumnList") dropdownMenuColumnList!: ElementRef;
  documentClickListener: (() => void) | null = null;

  // * Calculating pagination
  @ViewChild("tableContainer") tableContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren("tableRow") tableRows?: QueryList<
    ElementRef<HTMLTableRowElement>
  >;
  private resizeObserver?: ResizeObserver;
  private rowResizeObserver?: ResizeObserver;
  pageIndex = signal(0); // Current page index
  rowsPerPage = signal(3); // Default rows per page
  filteredFields = computed(() => {
    const allClaimedFields = this.allClaimedFields();
    const columnFilterMap = this.selectedColumnFilterValue();

    // Apply filtering based on selectedColumnFilterValue
    const filtered = allClaimedFields.filter((field) => {
      for (const [column, filterValue] of Object.entries(columnFilterMap)) {
        if (filterValue === null || filterValue === undefined) {
          continue; // No filter applied for this column
        }
        const fieldValue = field[column as keyof ClaimedField];
        if (String(fieldValue) !== String(filterValue)) {
          return false; // Field does not match the filter
        }
      }
      return true; // Field matches all filters
    });

    return filtered;
  });
  startIndex = computed(() => {
    return this.pageIndex() * this.rowsPerPage();
  });
  endIndex = computed(() => {
    const calculatedEnd = this.startIndex() + this.rowsPerPage();
    return Math.min(calculatedEnd, this.filteredFields().length);
  });
  paginatedData = computed(() => {
    return this.filteredFields().slice(this.startIndex(), this.endIndex());
  });

  // Dictionary of filtering options for each column - for dropdown only
  columnValueFilterOptions = computed<
    Partial<Record<string, ColumnFilterOptions>>
  >(() => {
    const fields = this.filteredFields();
    const visibleColumns = Object.entries(this.columnVisibility())
      .filter(([_, visible]) => visible)
      .map(([key, _]) => key as keyof ClaimedField);
    const optionsDict: Partial<
      Record<keyof ClaimedField, ColumnFilterOptions>
    > = {};

    visibleColumns.forEach((column) => {
      const uniqueValues: { [key: string]: any } = {};
      fields.forEach((field) => {
        const value = field[column];
        // For is_deficit_irrigation, include null values as empty string option
        if (
          column === "is_deficit_irrigation" &&
          (value === null || value === undefined)
        ) {
          uniqueValues["null"] = "";
        } else if (value !== null && value !== undefined) {
          uniqueValues[String(value)] = this.getColumnValue(column, field);
        }
      });
      optionsDict[column] = Array.from(Object.entries(uniqueValues)).map(
        ([key, value]) => {
          return { key: key, label: value };
        },
      );
    });
    return optionsDict;
  });
  selectedColumnFilterValue = model<Partial<Record<string, any>>>({}); // store the final filtering criteria (also output of the dropdown)
  onSelectedColumnFilterChange(column: string, value: any) {
    if (!(column in this.columnVisibility())) {
      console.warn(`Column ${column} not found in column visibility settings.`);
      return;
    }
    this.selectedColumnFilterValue.update((current) => ({
      ...current,
      [column]: value,
    }));
  }
  filterChipList = computed<Array<{ column: string; value: any }>>(() =>
    Object.entries(this.selectedColumnFilterValue())
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([c, v]) => ({ column: c, value: v })),
  );
  clearAllFilters = () => {
    this.selectedColumnFilterValue.set({});
  };

  toggleColumnVisibility(column: string) {
    if (!(column in this.columnVisibility())) {
      console.warn(`Column ${column} not found in column visibility settings.`);
      return;
    }

    this.columnVisibility.update((current) => ({
      ...current,
      [column]: !current[column as keyof ClaimedField],
    }));
  }

  constructor() {
    effect(() => {
      // Update the page index when the user selected field id changes
      if (this.userFieldSelectedId() === null) {
        this.pageIndex.set(0);
        return;
      }
      const rowIdx = this.filteredFields().findIndex(
        (field) => field.id === this.userFieldSelectedId(),
      );
      if (rowIdx === -1) {
        // console.warn('Selected field id not found in the current filtered fields.');
        this.pageIndex.set(0);
        return;
      }
      const correctPageIdx = Math.floor(rowIdx / this.rowsPerPage());
      this.pageIndex.set(correctPageIdx);
    });

    // If the visibility of columns changes, we have to reset a lot like the filters
    effect(() => {
      // Reset selected column filter values
      const visibleColumns = Object.entries(this.columnVisibility())
        .filter(([_, visible]) => visible)
        .map(([key, _]) => key);
      this.selectedColumnFilterValue.update((current) => {
        const entriesToKeep = Object.entries(current).filter(([key, _]) =>
          visibleColumns.includes(key),
        );
        return Object.fromEntries(entriesToKeep);
      });
    });
  }

  ngOnInit() {
    // It can make sure when TimeRange Dropdown is openning, click other area could close the Dropdown.
    this.documentClickListener = this.renderer.listen(
      "document",
      "click",
      (event: Event) => {
        const targetElement = event.target as HTMLElement;
        if (
          !this.dropdownButtonColumnList.nativeElement.contains(
            targetElement,
          ) &&
          !this.dropdownMenuColumnList.nativeElement.contains(targetElement)
        ) {
          this.columnListDropdownOpen = false;
        }
      },
    );
  }

  /**
   * renders the cell value based on the column type
   */
  renderCell(column: string, field: ClaimedField) {
    switch (column) {
      case "total_target_water":
        return `${field.total_target_water} ${field.total_target_water_unit} (${field.total_target_type})`;
      case "crop_type_01":
      case "crop_type_02":
      case "crop_type_03":
        return !field[column as keyof ClaimedField] ?
            "None"
          : ` ${field[column as keyof ClaimedField]}
          (${this.formatDate(
            field[
              `ls_start_${column.split("_")[2]}` as
                | "ls_start_01"
                | "ls_start_02"
                | "ls_start_03"
            ],
          )} - ${this.formatDate(
            field[
              `ls_end_${column.split("_")[2]}` as
                | "ls_end_01"
                | "ls_end_02"
                | "ls_end_03"
            ],
          )})`;
      default:
        return this.getColumnValue(column, field);
    }
  }

  // ! Pagination section
  ngAfterViewInit() {
    // Set up ResizeObserver to watch the table container
    if (this.tableContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updatePagination();
      });
      this.resizeObserver.observe(this.tableContainer.nativeElement);
      // Initial calculation
      this.updatePagination();
    } else {
      console.warn(
        "[Pagination] Table container not available in ngAfterViewInit",
      );
    }

    // Set up ResizeObserver for the first row to detect row height changes
    this.rowResizeObserver = new ResizeObserver(() => {
      this.updatePagination();
    });

    // Watch for changes in table rows (added/removed)
    this.tableRows?.changes.subscribe(() => {
      // Observe the first row when rows change
      if (this.tableRows?.first) {
        this.rowResizeObserver?.disconnect();
        this.rowResizeObserver?.observe(this.tableRows.first.nativeElement);
      }
      // Use requestAnimationFrame to ensure DOM is updated before reading heights
      requestAnimationFrame(() => this.updatePagination());
    });

    // Observe the first row initially
    if (this.tableRows?.first) {
      this.rowResizeObserver.observe(this.tableRows.first.nativeElement);
    }
  }

  updatePagination() {
    if (!this.tableContainer) {
      console.warn(
        "[Pagination] Table container not available for pagination calculation.",
      );
      return;
    }
    const rowHeight = this.tableRows?.first?.nativeElement.clientHeight || 65; // Fallback row height
    const containerHeight = this.tableContainer.nativeElement.clientHeight;
    const rowsThatFit = Math.floor(containerHeight / rowHeight) - 1;
    const finalRows = rowsThatFit > 0 ? rowsThatFit : 1;
    this.rowsPerPage.set(finalRows);
  }

  formatDate(date: Date | null): string {
    if (!date) {
      return "N/A";
    }
    return moment.utc(date).format("YYYY-MM-DD");
  }

  previousPage() {
    if (this.pageIndex() > 0) {
      this.pageIndex.set(this.pageIndex() - 1);
    }
  }
  nextPage() {
    if (this.endIndex() < this.filteredFields().length) {
      this.pageIndex.set(this.pageIndex() + 1);
    }
  }
  /////// End Pagination section /////

  // Method to convert JSON to CSV
  convertToCsv(json: ClaimedField[]): string {
    const visibleColumns = Object.entries(this.columnVisibility())
      .filter(([_, visible]) => visible)
      .map(([key, _]) => key);

    const headers = visibleColumns.map((f) => this.getColumnLabel(f)).join(",");
    const rows = json.map((obj) =>
      visibleColumns
        .map((field) => {
          if (field === "acres") return formatNumber(obj[field], 1);
          if (field.includes("ls_start") || field.includes("ls_end")) {
            // Convert the date using Moment.js to 'yyyy-mm-dd' format
            if (field in obj && obj[field as keyof typeof obj]) {
              return moment
                .utc(obj[field as keyof typeof obj])
                .format("YYYY-MM-DD")
                .toString();
            }
          }
          return obj[field as keyof ClaimedField] ?? ""; // Otherwise, keep the original value
        })
        .map(escapeCSV)
        .join(","),
    );
    return [headers, ...rows].join("\n");
  }
  downloadData() {
    if (this.filteredFields().length > 0) {
      const csvData = this.convertToCsv(this.filteredFields());
      const blob = new Blob([csvData], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      // Create a temporary anchor element and trigger the download
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "claimed_fields.csv";
      anchor.click();
      // Clean up
      window.URL.revokeObjectURL(url);
      anchor.remove();
    }
  }

  ngOnDestroy() {
    this.documentClickListener?.();
    this.resizeObserver?.disconnect();
    this.rowResizeObserver?.disconnect();
  }
}
